import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthLayout from '../auth/AuthLayout';

export default function SuccessScreen() {
  const navigate = useNavigate();
  const { user, family, loading } = useAuth();
  const [copied, setCopied] = useState(false);

  // Redirect if not logged in or no family
  useEffect(() => {
    if (!loading && (!user || !family)) {
      navigate('/signup');
    }
  }, [user, family, loading, navigate]);

  const copyInviteCode = async () => {
    if (family?.invite_code) {
      try {
        await navigator.clipboard.writeText(family.invite_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  if (loading || !family) {
    return (
      <AuthLayout>
        <div className="flex justify-center py-8">
          <svg className="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="text-center space-y-6">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <div>
          <h2 className="text-2xl font-bold text-gray-900">Welcome to Chronicle!</h2>
          <p className="mt-2 text-gray-600">
            Your family <strong>{family.name}</strong> has been created.
          </p>
        </div>

        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-6">
          <p className="text-sm text-gray-500 mb-2">Your Family Invite Code</p>
          <div className="flex items-center justify-center gap-3">
            <span className="text-3xl font-bold font-mono tracking-wider text-primary-600">
              {family.invite_code}
            </span>
            <button
              onClick={copyInviteCode}
              className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
              title="Copy invite code"
            >
              {copied ? (
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
          {copied && (
            <p className="text-sm text-green-600 mt-2">Copied to clipboard!</p>
          )}
        </div>

        <div className="bg-blue-50 rounded-lg p-4 text-left">
          <h3 className="font-medium text-blue-900 mb-2">Next Steps</h3>
          <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
            <li>Share the invite code with your family members</li>
            <li>Have them download the Chronicle app</li>
            <li>They'll enter the code and their name to join</li>
            <li>Start collecting memories together!</li>
          </ol>
        </div>

        <div className="space-y-3">
          <a
            href="/"
            className="btn-primary w-full py-3 block"
          >
            Go to Admin Dashboard
          </a>
          <button
            onClick={() => {
              const text = `Join our family on Chronicle! Use invite code: ${family.invite_code}`;
              if (navigator.share) {
                navigator.share({ text });
              } else {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }
            }}
            className="btn-secondary w-full py-3"
          >
            Share Invite Code
          </button>
        </div>

        <p className="text-sm text-gray-500">
          We've also sent a welcome email with your invite code.
        </p>
      </div>
    </AuthLayout>
  );
}
